import Immutable from 'immutable';
import { mapDataToAnswers, getLastAnswer } from 'utilities/RASuitabilityTestHelper';
import actionTypes from 'constants/RAConstants/actionTypes';
import { MONTHS, SUSTAINABILITY_LEVEL } from 'constants/RAConstants/index';

const initialState = Immutable.fromJS({
    answers: Immutable.OrderedMap({
        101: '',
        102: '',
        103: '',
        104: '',
        105: '',
        106: '',
        107: '',
        108: '',
        109: '',
        110: '',
        111: '',
        112: '',
        113: '',
        114: '',
        115: '',
        116: '',
        217: '',
        218: 5,
        219: 15,
        320: '',
        321: '',
        322: 1,
        323: '',
        401: '',
        402: '',
        403: '',
        404: '',
        405: '',
        500: '',
        701: 10000,
    }),
    validations: {},
    coherenceError: {
        error: false,
        message: '',
        allowToPass: true,
    },
    accordionStep: {},
    error: null,
    isFetchingQuestions: false,
    isFetchingResults: false,
    isSendingTest: false,
    questions: {},
    lastAnswer: {
        step: {
            category: 0,
            step: 0,
        },
        stepIndex: 1,
    },
    readOnly: false,
    success: false,
    testCompleted: false,
    results: null,
    isFetchingHistoricalTests: false,
    fetchedHistoricalTest: false,
    dropValue: MONTHS,
    isValidOTP: false,
    wasOTPValidated: false,
    loadingReloadOTPCode: false,
    isSendingTestEmail: false,
    testEmailSent: false,
    testEmailError: '',
    isSigningTest: false,
    signTestError: '',
    signTestFailed: false,
    signTestSuccess: false,
});

const accordionIcons = {
    // TODO: Map the right icons
    answer1: 'icon-bank-deposits',
    answer2: 'icon-fixed-income',
    answer3: 'icon-variable-income',
    answer4: 'icon-funds-plans-insurance',
    answer5: 'icon-products-with-warranty',
    answer6: 'icon-products-without-warranty',
    answer7: 'icon-convertible-bonds',
    answer8: 'icon-participates-preferred',
    answer9: 'icon-real-estate-products',
    answer10: 'icon-prod-venture-capital',
    answer11: 'icon-derivatives-market',
    answer12: 'icon-derivatives-otc',
};

const mapRadioButtons = answer => ({
    id: answer.id,
    label: {
        title: answer.title,
        description: answer.description,
    },
});

const mapAccordionButtons = button => ({
    id: button.id,
    label: button.title,
});

const mapAccordionRows = (row, index, questions) => ({
    id: `${index + 106}`,
    title: row.title,
    icon: accordionIcons[row.id],
    text: row.description,
    buttons: questions[index + 5].answerOptions.map(button => mapAccordionButtons(button)),
});

const mapAccordionStep = questions => ({
    title: questions[4]?.title ?? '',
    subtitle: questions[4]?.subtitle ?? '',
    expandedRowId: null,
    rows: (questions[4]?.answerOptions ?? []).map((row, index) => mapAccordionRows(row, index, questions)),
});

const mapSustainabilityAnswers = answer => {
    const levels = {
        answer1: SUSTAINABILITY_LEVEL.NEUTRAL,
        answer2: SUSTAINABILITY_LEVEL.INTERMEDIATE,
        answer3: SUSTAINABILITY_LEVEL.ADVANCED,
    };

    return {
        id: answer.id,
        text: answer.title,
        description: answer.description,
        level: levels[answer.id],
    };
};

const mapQuestions = questions => {
    const mappedQuestions = {
        101: {
            title: questions[0]?.title ?? '',
            answers: questions[0]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        102: {
            title: questions[1]?.title ?? '',
            answers: questions[1]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        103: {
            title: questions[2]?.title ?? '',
            answers: questions[2]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        104: {
            title: questions[3]?.title ?? '',
            answers: questions[3]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        217: {
            title: questions[15]?.title ?? '',
            answers: questions[15]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        218: {
            title: questions[16]?.title ?? '',
        },
        219: {
            title: questions[17]?.title ?? '',
            subtitle: questions[17]?.description ?? '',
        },
        320: {
            title: questions[18]?.title ?? '',
        },
        322: {
            title: questions[20]?.title ?? '',
            answers: [
                {
                    id: 1,
                    label: questions[20]?.answerOptions?.[3]?.title ?? '',
                },
                {
                    id: 2,
                    label: questions[20]?.answerOptions?.[2]?.title ?? '',
                },
                {
                    id: 3,
                    label: questions[20]?.answerOptions?.[1]?.title ?? '',
                },
                {
                    id: 4,
                    label: questions[20]?.answerOptions?.[0]?.title ?? '',
                },
            ],
        },
        323: {
            title: questions[21]?.title ?? '',
            answers: questions[21]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        401: {
            title: questions[22]?.title ?? '',
            answers: questions[22]?.answerOptions?.map(answer => mapSustainabilityAnswers(answer)) ?? [],
        },
        402: {
            title: questions[23]?.title ?? '',
            answers: questions[23]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        403: {
            title: questions[24]?.title ?? '',
            answers: questions[24]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        404: {
            title: questions[25]?.title ?? '',
            answers: questions[25]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        405: {
            title: questions[26]?.title ?? '',
            answers: questions[26]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
        500: {
            title: questions[27]?.title ?? '',
            answers: questions[27]?.answerOptions?.map(answer => mapRadioButtons(answer)) ?? [],
        },
    };

    return mappedQuestions;
};

const mapQuestionsToValidations = questions => {
    const validations = {};
    const filteredQuestions = questions.filter(
        question => question.answerOptions.some(answer => answer.unsuitable),
        []
    );

    filteredQuestions.forEach(question => {
        const answers = {};

        question.answerOptions
            .filter(answer => answer.unsuitable)
            .forEach(answer => {
                const previousQuestions = {};
                const unsuitable = answer.unsuitable || [];

                unsuitable.forEach(previousQuestion => {
                    if (previousQuestions.hasOwnProperty(previousQuestion.idQuestion)) {
                        previousQuestions[previousQuestion.idQuestion][previousQuestion.idAnswer] =
                            previousQuestion.errorCodeDescription;
                    } else {
                        previousQuestions[previousQuestion.idQuestion] = {
                            [previousQuestion.idAnswer]: previousQuestion.errorCodeDescription,
                        };
                    }
                });

                answers[answer.id] = previousQuestions;
            });

        validations[question.id] = answers;
    });

    return validations;
};

const updateExpandedRow = (payload, state) => {
    if (state.getIn(['accordionStep', 'expandedRowId']) === payload.expandedRowId) {
        payload = Object.assign({}, payload, {
            expandedRowId: null,
        });
    }

    return state.mergeDeepIn(['accordionStep'], payload);
};

const RASuitabilityTest = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.RA_SUITABILITY_TEST_EXPAND_ACCORDION_ROW:
            return updateExpandedRow(action.payload, state);
        case actionTypes.RA_SUITABILITY_GET_TEST_QUESTIONS_REQUEST:
            return state.merge({
                isFetchingQuestions: true,
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_QUESTIONS_SUCCESS:
            const { data } = action.payload;
            const questions = data.questions ?? [];
            const answers = data.answered?.nextAnswer ?? [];
            const answeredQuestionIds = answers.map(answer => answer.idQuestion);
            let lastAnswer = getLastAnswer(answeredQuestionIds);

            if (!lastAnswer) {
                lastAnswer = {
                    step: {
                        category: 0,
                        step: 0,
                    },
                    stepIndex: 0,
                };
            }

            return state
                .set('isFetchingQuestions', false)
                .set('questions', Immutable.fromJS(mapQuestions(questions)))
                .set('accordionStep', Immutable.fromJS(mapAccordionStep(questions)))
                .set('validations', Immutable.fromJS(mapQuestionsToValidations(questions)))
                .set('lastAnswer', Immutable.fromJS(lastAnswer))
                .mergeDeep({
                    answers: answers.length ? Immutable.OrderedMap(mapDataToAnswers(answers)) : state.get('answers'),
                });
        case actionTypes.RA_SUITABILITY_GET_TEST_QUESTIONS_FAILURE:
            return state.merge({
                error: action.payload.error,
                isFetchingQuestions: false,
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_RESULT_REQUEST:
            return state.merge({
                isFetchingResults: true,
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_RESULT_SUCCESS:
            return state.merge({
                results: action.payload.results,
                isFetchingResults: false,
                testCompleted: true,
                success: action.payload.results[0]?.passedTest ?? false,
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_RESULT_FAILURE:
            return state.merge({
                error: action.payload.error,
                isFetchingResults: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_SELECT_ACCORDION_BUTTON:
            return state.setIn(['answers', action.payload.rowId], action.payload.buttonId);
        case actionTypes.RA_SUITABILITY_TEST_RESET:
            return initialState;
        case actionTypes.RA_SUITABILITY_TEST_RESET_COHERENCE_ERROR:
            return state.mergeIn(['coherenceError'], {
                allowToPass: false,
                error: false,
                message: '',
            });
        case actionTypes.RA_SET_ERROR_COHERENCE_VALIDATION:
            return state.mergeIn(['coherenceError'], {
                allowToPass: action.payload.allowToPass,
                error: true,
                message: action.payload.message,
            });
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_ANSWER_BY_QUESTION_ID:
            return state.setIn(['answers', action.payload.questionId], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_VOLATILITY_INPUT_VALUE:
            return state.setIn(['answers', '701'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_VOLATILITY_VALUE:
            return state.setIn(['answers', '219'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_CAPITAL:
            return state.setIn(['answers', '322'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_INCOMING_VALUE:
            return state.setIn(['answers', '320'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_OUTGOING_VALUE:
            return state.setIn(['answers', '321'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_EXPENSES_VALUE:
            return state.setIn(['answers', '323'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_INVESTMENT_HORIZON_SLIDER:
            return state.setIn(['answers', '218'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_IN_PROGRESS:
            return state.merge({
                isSendingTest: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_SUCCESS:
            return state.merge({
                isSendingTest: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_VALIDATE_OTP_SUCCESS:
            return state.merge({
                isValidOTP: action.payload.isValidOTP,
                wasOTPValidated: action.payload.wasOTPValidated,
                loadingReloadOTPCode: action.payload.loadingReloadOTPCode,
            });
        case actionTypes.RA_SUITABILITY_TEST_VALIDATE_OTP_ERROR:
            return state.merge({
                isValidOTP: action.payload.isValidOTP,
                wasOTPValidated: action.payload.wasOTPValidated,
                loadingReloadOTPCode: action.payload.loadingReloadOTPCode,
            });
        case actionTypes.RA_SUITABILITY_TEST_RELOAD_OTP:
            return state.merge({
                loadingReloadOTPCode: action.payload.loadingReloadOTPCode,
            });
        case actionTypes.RA_SUITABILITY_TEST_RESET_OTP:
            return state.merge({
                wasOTPValidated: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_RESET_ANSWERS:
            return state.merge({
                answers: initialState.get('answers'),
                lastAnswer: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_RESET_TEST_COMPLETED:
            return state.merge({
                testCompleted: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_EMAIL_IN_PROGRESS:
            return state.merge({
                isSendingTestEmail: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_EMAIL_SUCCESS:
            return state.merge({
                testEmailSent: true,
                isSendingTestEmail: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_EMAIL_FAILURE:
            return state.merge({
                isSendingTestEmail: false,
                testEmailError: action.payload.error,
                testEmailSent: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_SIGN_TEST_REQUEST:
            return state.merge({
                isSigningTest: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_SIGN_TEST_FAILURE:
            return state.merge({
                isSigningTest: false,
                signTestError: action.payload.error,
                signTestFailed: true,
                signTestSuccess: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_SIGN_TEST_SUCCESS:
            return state.merge({
                isSigningTest: false,
                signTestError: '',
                signTestFailed: false,
                signTestSuccess: true,
            });
        case actionTypes.RA_FILL_DEFAULT_ANSWERS_ACCORDION: {
            const answers = state.get('answers').toJS();
            const accordionStep = state.get('accordionStep');
            const rows = accordionStep.get('rows');
            const filledAnswers = rows.reduce((acc, answer) => {
                return { ...acc, [answer.get('id')]: 'answer1' };
            }, {});

            return state.merge({
                answers: { ...answers, ...filledAnswers },
            });
        }
        default:
            return state;
    }
};

module.exports = RASuitabilityTest;
